import { z } from 'zod';
import { DocId, IsoTimestamp, TenantId } from '../common.js';
import { AgentCitation } from './agent.js';
/**
 * Slice 3b chat-history wedge — wire schemas + Firestore document shapes.
 *
 * Firestore paths:
 *   tenants/{tenantId}/users/{userId}/chats/{chatId}
 *   tenants/{tenantId}/users/{userId}/chats/{chatId}/messages/{messageId}
 *
 * See ~/.gstack/projects/kennisbank-suppohrt/WitteA-main-design-20260429-071106.md
 * for the full design doc + eng-review revisions.
 */
// ───────── Document field schemas ─────────────────────────────────────────
/**
 * Title lifecycle:
 *   pending     — chat just created, no title call has resolved yet
 *   generating  — Gemini Flash title call in flight (lease via titleGeneratedAt)
 *   generated   — title is final, no further auto-title work
 *
 * The design doc's Q4 self-heal: a chat with `titleStatus="pending"` OR
 * `titleStatus="generating"` AND lease > 30s old is fair game for a re-fire
 * on the next ask.
 */
export const ChatTitleStatus = z.enum(['pending', 'generating', 'generated']);
export const ChatMessageRole = z.enum(['user', 'assistant']);
/**
 * Persisted chat-message source citation. Stored on assistant messages so the
 * case-file practitioner sees the exact Ring 1/Ring 2 sources that grounded
 * a 5-day-old answer. Reuses the `AgentCitation` wire shape verbatim — no
 * re-modelling, no schema drift across the backend↔agent↔storage boundary.
 */
export const ChatMessageSource = AgentCitation;
/**
 * Subcollection doc — one per turn (user or assistant).
 *
 * `tokensIn`/`tokensOut` mirror what `ai_usage_events` already records for
 * the assistant turn; storing them here lets the chat thread render token
 * usage in admin/debug views without joining BigQuery.
 */
export const ChatMessage = z.object({
    messageId: DocId,
    role: ChatMessageRole,
    content: z.string().min(1).max(10000),
    sources: z.array(ChatMessageSource).default([]),
    modelId: z.string().min(1).max(100).optional(),
    tokensIn: z.number().int().nonnegative().optional(),
    tokensOut: z.number().int().nonnegative().optional(),
    /**
     * Multi-turn debug surface. For assistant turns, the standalone Dutch
     * query the backend reformulator produced from the user's (often terse)
     * follow-up. Lets Linda see "your question was interpreted as: X" via
     * a future debug affordance. Absent on cold-start asks where no
     * reformulation happened.
     */
    reformulatedQuery: z.string().min(1).max(1000).optional(),
    createdAt: IsoTimestamp,
});
/**
 * Parent chat doc. `lastQuestion` + `lastAnswerPreview` are denormalised so
 * the list endpoint never needs to fan out into the messages subcollection
 * (eng-review section 4 — N+1 elimination).
 *
 * `lastAnswerPreview` is capped at 80 chars per the eng-review #9 PII smear
 * mitigation (was 240; reduced to limit clinical-context exposure in the
 * sidebar list).
 */
export const ChatSummary = z.object({
    chatId: DocId,
    userId: DocId,
    tenantId: TenantId,
    title: z.string().min(1).max(200),
    titleStatus: ChatTitleStatus,
    /** Lease for the in-flight title call (cross-model T1 fix). */
    titleGeneratedAt: IsoTimestamp.optional(),
    messageCount: z.number().int().nonnegative(),
    sourceCount: z.number().int().nonnegative(),
    lastQuestion: z.string().max(2000),
    lastAnswerPreview: z.string().max(80),
    /** Tombstone — null on live chats, set on user-initiated delete (1C-C). */
    deletedAt: IsoTimestamp.nullable().default(null),
    /**
     * Retentiehorizon — aanmaak + 30 dagen. De Cloud Scheduler-janitor
     * (`/api/internal/chat-retention-janitor`) verwijdert chats fysiek zodra
     * `expiresAt` is verstreken; zie ADR 0008. Optioneel omdat chats van vóór
     * de retentie-invoering het veld niet dragen; `createChat` zet het altijd.
     */
    expiresAt: IsoTimestamp.optional(),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
    schemaVersion: z.literal(1),
});
// ───────── Pagination cursor ──────────────────────────────────────────────
/**
 * Stable cursor across "Meer laden". Uses `(createdAt, chatId)` rather than
 * `(updatedAt, chatId)` — eng-review fix #4. updatedAt is mutated by every
 * write to the chat (new message, title arrival), so paginating on it
 * causes duplicates/skips while a parallel ask bumps the row.
 *
 * Encoded as a base64url JSON string on the wire so the frontend doesn't
 * have to reason about the shape.
 */
export const ChatCursor = z.object({
    createdAt: IsoTimestamp,
    chatId: DocId,
});
// ───────── Route request/response shapes ──────────────────────────────────
export const ListChatsRequest = z.object({
    /**
     * Page size. Default 50; capped at 100 to bound the response payload.
     */
    limit: z.coerce.number().int().min(1).max(100).default(50),
    /**
     * Opaque base64url cursor returned by a previous list response.
     * Absent on the first request.
     */
    cursor: z.string().min(1).max(500).optional(),
});
export const ListChatsResponse = z.object({
    chats: z.array(ChatSummary),
    nextCursor: z.string().min(1).max(500).nullable(),
});
export const GetChatResponse = z.object({
    chat: ChatSummary,
    messages: z.array(ChatMessage),
});
// ───────── Agent /v1/agent/title wire (eng-review 1A-B) ───────────────────
/**
 * POST /v1/agent/title — backend-to-agent call after the assistant message
 * has been written. Agent calls Gemini Flash with a 60-token Dutch prompt
 * and returns 4-6 word title. Returns `title: null` on English/refusal
 * fallback so the backend can leave `titleStatus="pending"` for self-heal.
 */
export const AgentTitleRequest = z.object({
    chatId: DocId,
    tenantId: TenantId,
    userId: DocId,
    firstQuestion: z.string().min(1).max(2000),
    firstAnswer: z.string().min(1).max(8000),
});
export const AgentTitleResponse = z.object({
    title: z.string().min(1).max(200).nullable(),
    modelId: z.string().min(1).max(100),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
});
//# sourceMappingURL=chat-history.js.map